import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { FormEvent } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../../shared/types/database";
import {
  persistAppLanguage,
  type AppLanguage
} from "../../shared/lib/languagePreference";
import {
  getAuthErrorTranslationKey,
  type AuthMode
} from "./authError";
import { normalizeContactPhone } from "./contactPhone";
import {
  getReturningHero,
  returningHeroSpriteIndex
} from "./returningHero";

interface AuthScreenProps {
  activeSessionEmail?: string | null;
  client: SupabaseClient<Database>;
  onFullLogout?: () => Promise<void>;
  onQuickReturn?: () => void;
}

export function AuthScreen({
  activeSessionEmail,
  client,
  onFullLogout,
  onQuickReturn
}: AuthScreenProps) {
  const { i18n, t } = useTranslation();
  const [mode, setMode] = useState<AuthMode>("login");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [returningHero] = useState(getReturningHero);
  const [showReturningChoice, setShowReturningChoice] = useState(Boolean(returningHero));
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canQuickReturn = Boolean(
    returningHero
    && activeSessionEmail
    && returningHero.email.toLowerCase() === activeSessionEmail.toLowerCase()
    && onQuickReturn
  );

  const changeMode = (nextMode: AuthMode) => {
    setMode(nextMode);
    setShowReturningChoice(false);
    setError(null);
    setMessage(null);
  };

  const continueAsReturningHero = () => {
    if (!returningHero) {
      return;
    }

    if (canQuickReturn) {
      onQuickReturn?.();
      return;
    }

    setMode("login");
    setEmail(returningHero.email);
    setPassword("");
    setShowReturningChoice(false);
    setError(null);
    setMessage(null);
  };

  const openLoginWithCredentials = async () => {
    setLoading(true);
    try {
      await onFullLogout?.();
    } catch {
      setError(t("authLogoutError"));
      setLoading(false);
      return;
    }

    setMode("login");
    setEmail("");
    setPassword("");
    setShowReturningChoice(false);
    setError(null);
    setMessage(null);
    setLoading(false);
  };

  const openRegistration = async () => {
    setLoading(true);
    try {
      await onFullLogout?.();
    } catch {
      setError(t("authLogoutError"));
      setLoading(false);
      return;
    }

    changeMode("register");
    setLoading(false);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (password.length < 8) {
      setError(t("authPasswordLength"));
      return;
    }

    if (mode === "register" && displayName.trim().length < 2) {
      setError(t("authNameLength"));
      return;
    }

    const contactPhone = mode === "register" ? normalizeContactPhone(phone) : null;
    if (mode === "register" && !contactPhone) {
      setError(t("authPhoneInvalid"));
      return;
    }

    setLoading(true);
    try {
      if (mode === "login") {
        const { error: authError } = await client.auth.signInWithPassword({
          email: email.trim(),
          password
        });
        if (authError) {
          throw authError;
        }
      } else {
        const locale = i18n.resolvedLanguage?.startsWith("uz") ? "uz" : "ru";
        const { data, error: authError } = await client.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: {
              display_name: displayName.trim(),
              locale,
              contact_phone: contactPhone
            }
          }
        });
        if (authError) {
          throw authError;
        }
        if (!data.session) {
          setMessage(t("authCheckEmail"));
        }
      }
    } catch (authError) {
      setError(t(getAuthErrorTranslationKey(authError, mode)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="onboarding-shell">
      <div className="onboarding-scene" aria-hidden="true">
        <span className="onboarding-scene__hero" />
        <span className="onboarding-scene__bot" />
      </div>
      <section className="auth-card" aria-labelledby="auth-title">
        <LanguageSwitch />
        {showReturningChoice && returningHero ? (
          <>
            <button
              className="auth-card__mark auth-card__mark--returning"
              type="button"
              aria-label={t("authContinueAs", { name: returningHero.displayName })}
              disabled={loading}
              onClick={continueAsReturningHero}
            >
              <span
                className={`auth-card__hero-sprite auth-card__hero-sprite--${returningHeroSpriteIndex(returningHero.heroSlug)}`}
                aria-hidden="true"
              />
            </button>
            <h1 id="auth-title">{t("authReturningWelcome", { name: returningHero.displayName })}</h1>
            <p>{t("authReturningLead")}</p>
            <div className="auth-returning-actions">
              <button
                className="primary-game-button"
                type="button"
                disabled={loading}
                onClick={continueAsReturningHero}
              >
                {t("authContinueAs", { name: returningHero.displayName })}
              </button>
              <button
                className="secondary-game-button"
                type="button"
                disabled={loading}
                onClick={() => void openLoginWithCredentials()}
              >
                {t("authLoginWithCredentials")}
              </button>
              <button
                className="auth-register-link"
                type="button"
                disabled={loading}
                onClick={() => void openRegistration()}
              >
                {t("authCreateNewHero")}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="auth-card__mark" aria-hidden="true">
              <img
                className="auth-card__logo"
                src="/icons/app-icon-192.png"
                alt=""
              />
            </div>
            <h1 id="auth-title">{t(mode === "login" ? "authWelcome" : "authCreateHero")}</h1>
            <p>{t(mode === "login" ? "authLoginLead" : "authRegisterLead")}</p>

            <div className="auth-tabs" role="tablist" aria-label={t("authMode")}>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "login"}
            onClick={() => changeMode("login")}
          >
            {t("authLoginTab")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            onClick={() => changeMode("register")}
          >
            {t("authRegisterTab")}
          </button>
            </div>

            <form
          className="auth-form"
          noValidate
          onSubmit={(event) => {
            void submit(event);
          }}
        >
          {mode === "register" ? (
            <label>
              <span>{t("authHeroName")}</span>
              <input
                autoComplete="nickname"
                maxLength={40}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder={t("authHeroNamePlaceholder")}
                required
              />
            </label>
          ) : null}
          <label>
            <span>{t("authEmail")}</span>
            <input
              autoCapitalize="none"
              autoComplete="email"
              inputMode="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="hero@example.com"
              required
            />
          </label>
          {mode === "register" ? (
            <label>
              <span>{t("authPhone")}</span>
              <input
                autoComplete="tel"
                inputMode="tel"
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="+998 90 123 45 67"
                required
              />
              <small>{t("authPhoneHint")}</small>
            </label>
          ) : null}
          <label>
            <span>{t("authPassword")}</span>
            <input
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              minLength={8}
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
              required
            />
          </label>
          {error ? <p className="auth-message auth-message--error" role="alert">{error}</p> : null}
          {message ? <p className="auth-message auth-message--success" role="status">{message}</p> : null}
          <button className="primary-game-button" type="submit" disabled={loading}>
            {loading
              ? t("authLoading")
              : t(mode === "login" ? "authLoginAction" : "authRegisterAction")}
          </button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}

function LanguageSwitch() {
  const { i18n } = useTranslation();
  const isUzbek = i18n.resolvedLanguage?.startsWith("uz") ?? true;
  const changeLanguage = (language: AppLanguage) => {
    persistAppLanguage(language);
    void i18n.changeLanguage(language);
  };

  return (
    <div className="language-switch" aria-label="Language">
      <button
        type="button"
        aria-pressed={isUzbek}
        onClick={() => changeLanguage("uz-Latn")}
      >
        UZ
      </button>
      <button
        type="button"
        aria-pressed={!isUzbek}
        onClick={() => changeLanguage("ru")}
      >
        RU
      </button>
    </div>
  );
}
